import TableValidationFlag from '../TableValidationFlag';
import React, { useRef, useState } from 'react';
import { TableCell, Tooltip, Box, Stack, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getFieldDisplayName, formatValueForDisplay } from 'src/helper/quoteComparison';

interface ComparisonTableCellProps {
  leading?: React.ReactNode;
  component?: 'td' | 'th' | 'div';
  acceptedLabel?: string;
  acceptActionLabel?: string;
  rejectActionLabel?: string;
  value: any;
  field: string;
  fieldLabel?: string;
  requestedValue?: any;
  originalValue?: any;
  hasChanged: boolean;
  isDeleted?: boolean;
  cellSx?: any;
  onClick?: () => void;
  iconsHidden?: boolean;
  onAcceptChange?: (newValue: any) => void;
  onDecision?: (decision: 'accept' | 'reject') => void | boolean | Promise<unknown>;
  decision?: 'accept' | 'reject' | 'custom';
  decisionBusy?: boolean;
  allowDecisionChange?: boolean;
  rejectionLabel?: string;
  onHideIcons?: () => void;
  formatDisplayValue?: (value: any) => React.ReactNode;
  validationError?: string;
}

/** Client revision review: orange until reviewed, green accepted, red rejected (from saved cell decisions). */
export const clientRequestDecisionColor = (decision?: string) =>
  decision === 'accept' ? '#c8e6c9' : decision === 'reject' ? '#ffcdd2' : decision === 'custom' ? '#e3f2fd' : '#ffd8a8';

const ComparisonTableCell: React.FC<ComparisonTableCellProps> = ({
  leading,
  component,
  acceptedLabel = 'Applied to the quote',
  acceptActionLabel = 'Accept new value',
  rejectActionLabel = 'Reject change',
  value,
  field,
  fieldLabel,
  requestedValue,
  originalValue,
  hasChanged,
  isDeleted = false,
  cellSx = {},
  onClick,
  iconsHidden = false,
  onAcceptChange,
  onDecision,
  decision,
  decisionBusy = false,
  allowDecisionChange = false,
  rejectionLabel = 'Rejected; current value retained',
  onHideIcons,
  formatDisplayValue,
  validationError
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  const isReviewFocus = (target: EventTarget | null) => target instanceof Node
    && Boolean(cellRef.current?.contains(target) || reviewRef.current?.contains(target));
  const handleReviewBlur = (event: React.FocusEvent) => {
    if (!isReviewFocus(event.relatedTarget)) setTooltipOpen(false);
  };
  const displayValue = isDeleted ? value : value;
  const formatDisplay = formatDisplayValue ?? ((item) => formatValueForDisplay(item, field));
  const displayRequestedValue = formatDisplay(requestedValue);

  const isHighlighted = hasChanged && !isDeleted;

  const baseStyle = {
    ...cellSx,
    backgroundColor: isHighlighted && decision
      ? clientRequestDecisionColor(decision)
      : validationError
      ? '#dfc39b'
      : isHighlighted
        ? clientRequestDecisionColor(decision)
        : cellSx.backgroundColor || 'inherit',
    textDecoration: isDeleted ? 'line-through' : 'none',
    opacity: isDeleted ? 0.6 : 1,
    transition: 'all 0.2s ease'
  };

  const handleCheckClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDecision) {
      if (await onDecision('accept') === true) setTooltipOpen(false);
      return;
    }
    onHideIcons?.();
    setTooltipOpen(false);
    if (onAcceptChange) {
      setTimeout(() => {
        onAcceptChange(requestedValue);
      }, 0);
    }
  };

  const handleCrossClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDecision) {
      if (await onDecision('reject') === true) setTooltipOpen(false);
      return;
    }
    onHideIcons?.();
    setTooltipOpen(false);
  };

  const tooltipContent = isHighlighted && requestedValue !== undefined ? (
    <Stack ref={reviewRef} spacing={1}
      sx={{ color: 'white' }}
      onFocus={() => setTooltipOpen(true)} onBlur={handleReviewBlur}>
      {validationError && <Box sx={{ color: 'white' }}>{validationError}</Box>}
      <Box>
        <strong>{fieldLabel ?? getFieldDisplayName(field)}</strong>
      </Box>
      <Box sx={{ fontSize: '0.9em' }}>
        <strong>New:</strong> {displayRequestedValue}
      </Box>
      <Box sx={{ fontSize: '0.9em' }}>
        <strong>Original:</strong> {formatDisplay(originalValue === undefined ? value : originalValue)}
      </Box>
      {decision && <Box data-client-request-decision={decision} sx={{ fontSize: '0.9em', fontWeight: 700 }}>
        {decision === 'accept' ? acceptedLabel : decision === 'custom' ? 'Custom value saved' : rejectionLabel}
      </Box>}
      {(onAcceptChange || onDecision) && !iconsHidden && (!decision || allowDecisionChange) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 0.5 }}
          onMouseDown={event => event.preventDefault()}>
          <IconButton
            size="small"
            onClick={handleCheckClick}
            disabled={decisionBusy}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(76, 175, 80, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.7)'
              },
              p: 0.5
            }}
            title={acceptActionLabel}
          >
            <CheckIcon sx={{ fontSize: '18px' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCrossClick}
            disabled={decisionBusy}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(244, 67, 54, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.7)'
              },
              p: 0.5
            }}
            title={rejectActionLabel}
          >
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
      )}
    </Stack>
  ) : validationError ? (
    <Box sx={{ color: 'white' }}>{validationError}</Box>
  ) : isDeleted ? (
    <Box sx={{ color: 'white' }}>Shift was deleted in history</Box>
  ) : null;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={event => {
        if (event.type !== 'keydown' && reviewRef.current?.contains(document.activeElement)) return;
        setTooltipOpen(false);
      }}
      disableInteractive={!isHighlighted}
    >
      <TableCell component={component} ref={cellRef}
        data-request-field={field}
        tabIndex={hasChanged ? 0 : -1}
        onFocus={() => setTooltipOpen(true)} onBlur={handleReviewBlur}
        onKeyDown={event => {
          if (event.key === 'Tab' && !event.shiftKey && tooltipOpen) {
            const action = reviewRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
            if (action) { event.preventDefault(); action.focus(); }
          }
        }}
        data-quote-column={field === 'callTime' ? 'call-time' : field === 'notes' ? 'notes' : field} data-document-revision={hasChanged || isDeleted ? "changed" : undefined}
        data-client-request-decision={decision}
        align="center"
        sx={{
          ...baseStyle,
          position: 'relative',
          "&:focus": { outline: "3px solid #b58200", outlineOffset: -3 },
          cursor: onClick ? 'pointer' : 'inherit',
          padding: component === 'div' ? 0 : '4px 6px'
        }}
        onClick={onClick}
        aria-invalid={Boolean(validationError)}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          {leading}
          <Box component="span">{formatDisplay(displayValue)}</Box>
          {isHighlighted && !decision && (
            <Box
              component="span"
              title="Client requested a change"
              aria-label="Client requested a change"
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#9a6700',
                flex: '0 0 auto'
              }}
            />
          )}
          {validationError && <TableValidationFlag message={`${getFieldDisplayName(field)}: ${validationError}`} />}
        </Stack>
      </TableCell>
    </Tooltip>
  );
};

export default ComparisonTableCell;
